import Button from "../../../../../common/components/button";
import TableName from "../../../../components/common/tableName";
import Input from "../../../../components/common/input";
import SelectBox from "../../../../components/common/selectBox";
import { ReactComponent as UploadImage } from "../../../../assets/icons/uploadImage.svg";
import { useGetGoodsCategory } from "../../../../../common/hooks/console/management/goods/useGetGoodsCategory";
import React, { useEffect, useState } from "react";
import useToastStore from "../../../../store/useToastStore";
import { usePostGoods } from "../../../../../common/hooks/console/management/goods/usePostGoods";
import { ReactComponent as CloseIcon } from "../../../../assets/icons/closeWhiteIcon.svg";
import { ReactComponent as PlusIcon } from "../../../../assets/icons/plusIcon.svg";
import { ReactComponent as CloseBlackIcon } from "../../../../assets/icons/closeIcon.svg";
import { ReactComponent as UploadOnImage } from "../../../../assets/icons/uploadOnImage.svg";
import { useSystemPopStore } from "../../../../store/useSystemPopStore";
import noticeImage from "../../../../assets/images/notice.png";
import { useGetGoodsCode } from "../../../../../common/hooks/console/management/goods/useGetGoodsCode";

const NewGoods = ({ onProductCreated }: { onProductCreated: (productIdx: number) => void }) => {
    const { addToast } = useToastStore();
    const { showPop } = useSystemPopStore();

    const [selectedCode, setSelectedCode] = useState<string>("");
    const [selectedCategory, setSelectedCategory] = useState<string>("");
    const [productName, setProductName] = useState<string>("");
    const [productAmount, setProductAmount] = useState<number>(0);
    const [productContent, setProductContent] = useState<string>("");
    const [productMemo, setProductMemo] = useState<string>("");
    const [productImages, setProductImages] = useState<File[]>([]);
    const [noticeImages, setNoticeImages] = useState<(File | string)[]>([noticeImage]);
    const [categoryError, setCategoryError] = useState<boolean>(false);
    const [productNameError, setProductNameError] = useState<boolean>(false);
    const [imageUploadPop, setImageUploadPop] = useState(false);
    const [imageProductUploadPop, setImageProductUploadPop] = useState(false);
    const [tempProductImages, setTempProductImages] = useState<File[]>([]);
    const [tempNoticeImages, setTempNoticeImages] = useState<(File | string)[]>([noticeImage]);
    const [previewImage, setPreviewImage] = useState<string | null>(null);

    const { data: categoryData } = useGetGoodsCategory();
    const { data: codeData } = useGetGoodsCode();

    const { mutate } = usePostGoods(productIdx => {
        addToast({
            type: "success",
            message: "상품이 신규등록되었습니다.",
        });
        onProductCreated(productIdx);
    });

    const categoryOptions = categoryData
        ? categoryData.map(cat => ({
              value: cat.product_category_idx.toString(),
              label: cat.category_name,
          }))
        : [];

    const codeOptions = codeData
        ? codeData.map(code => ({
              value: code.product_code_idx.toString(),
              label: code.product_code_name + " / " + code.product_code,
          }))
        : [];

    const handleSave = () => {
        let hasError = false;

        if (!selectedCategory) {
            setCategoryError(true);
            hasError = true;
        }
        if (!productName.trim()) {
            setProductNameError(true);
            hasError = true;
        }
        if (hasError) return;

        mutate({
            product_category_idx: Number(selectedCategory),
            product_code_idx: Number(selectedCode),
            product_name: productName,
            product_amount: Number(productAmount),
            product_content: productContent,
            product_memo: productMemo,
            product_image: productImages,
            notice_image: noticeImages.filter((img): img is File => img instanceof File),
        });
    };

    const handleCancel = () => {
        setSelectedCategory("");
        setProductName("");
        setProductAmount(0);
        setProductContent("");
        setProductMemo("");
        setProductImages([]);
        setNoticeImages([]);
        setCategoryError(false);
        setProductNameError(false);
    };

    const handlePreviewImage = (image: string | File) => {
        const imageUrl = typeof image === "string" ? image : URL.createObjectURL(image);
        setPreviewImage(imageUrl);
    };

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>, type: "product" | "notice") => {
        if (e.target.files) {
            const files = Array.from(e.target.files);

            if (type === "product") {
                const newFiles = [...tempProductImages, ...files];
                if (newFiles.length > 4) {
                    showPop({
                        title: "이미지는 최대 4장까지 <br /> 등록할 수 있습니다.",
                        type: "negative",
                        onRightClick: () => {},
                    });
                    return;
                }
                setTempProductImages(newFiles);
            } else {
                const newFiles = [...tempNoticeImages, ...files];
                if (newFiles.length > 4) {
                    showPop({
                        title: "이미지는 최대 4장까지 <br /> 등록할 수 있습니다.",
                        type: "negative",
                        onRightClick: () => {},
                    });
                    return;
                }
                setTempNoticeImages(newFiles);
            }
        }
    };

    const handleConfirmImages = (type: "product" | "notice") => {
        if (type === "product") {
            setProductImages([...tempProductImages]);
            setImageProductUploadPop(false);
        } else {
            const uniqueImages = Array.from(new Set(tempNoticeImages));
            setNoticeImages([...uniqueImages]);
            setImageUploadPop(false);
        }
    };

    const handleRemoveImage = (index: number, type: "product" | "notice") => {
        if (type === "product") {
            setProductImages(prev => prev.filter((_, i) => i !== index));
            setTempProductImages(prev => prev.filter((_, i) => i !== index));
        } else {
            setNoticeImages(prev => prev.filter((_, i) => i !== index));
            setTempNoticeImages(prev => prev.filter((_, i) => i !== index));
        }
    };

    const handleDrop = (e: React.DragEvent<HTMLLabelElement>, type: "product" | "notice") => {
        e.preventDefault();
        const files = Array.from(e.dataTransfer.files);

        if (type === "product") {
            const newFiles = [...tempProductImages, ...files];
            if (newFiles.length > 4) {
                showPop({
                    title: "이미지는 최대 4장까지 <br /> 등록할 수 있습니다.",
                    type: "negative",
                    onRightClick: () => {},
                });
                return;
            }
            setTempProductImages(newFiles);
        } else {
            const newFiles = [...tempNoticeImages, ...files];
            if (newFiles.length > 4) {
                showPop({
                    title: "이미지는 최대 4장까지 <br /> 등록할 수 있습니다.",
                    type: "negative",
                    onRightClick: () => {},
                });
                return;
            }
            setTempNoticeImages(newFiles);
        }
    };

    const handleCloseImageUploadPop = (type: "product" | "notice") => {
        if (type === "product") {
            setTempProductImages([...productImages]);
            setImageProductUploadPop(false);
        } else {
            setTempNoticeImages([...noticeImages]);
            setImageUploadPop(false);
        }
    };
    useEffect(() => {
        const handlePaste = (e: ClipboardEvent) => {
            const items = e.clipboardData?.items;
            if (items) {
                const images: File[] = [];
                for (let i = 0; i < items.length; i++) {
                    if (items[i].kind === "file") {
                        const file = items[i].getAsFile();
                        if (file && file.type.startsWith("image/")) {
                            images.push(file);
                        }
                    }
                }
                if (images.length > 0) {
                    if (imageProductUploadPop) {
                        const newFiles = [...tempProductImages, ...images];
                        if (newFiles.length > 4) {
                            showPop({
                                title: "이미지는 최대 4장까지 <br /> 등록할 수 있습니다.",
                                type: "negative",
                                onRightClick: () => {},
                            });
                            return;
                        }
                        setTempProductImages(newFiles.slice(0, 4));
                    } else if (imageUploadPop) {
                        const newFiles = [...tempNoticeImages, ...images];
                        if (newFiles.length > 4) {
                            showPop({
                                title: "이미지는 최대 4장까지 <br /> 등록할 수 있습니다.",
                                type: "negative",
                                onRightClick: () => {},
                            });
                            return;
                        }
                        setTempNoticeImages(newFiles.slice(0, 4));
                    }
                }
            }
        };

        document.addEventListener("paste", handlePaste);
        return () => document.removeEventListener("paste", handlePaste);
    }, [imageProductUploadPop, imageUploadPop, tempProductImages, tempNoticeImages]);
    useEffect(() => {
        const convertNoticeImageToFile = async () => {
            if (typeof noticeImage === "string") {
                const response = await fetch(noticeImage);
                const blob = await response.blob();
                const file = new File([blob], "notice.png", { type: blob.type });
                setNoticeImages([file]);
                setTempNoticeImages([file]);
            }
        };

        convertNoticeImageToFile();
    }, []);

    return (
        <div className="bg-gray6 p-4 h-full">
            <div className="bg-white shadow-custom rounded-xl px-3 py-5 flex flex-col gap-5">
                <p className="text-xl font-bold ">상품 등록</p>
                <div className="flex flex-col gap-3">
                    <div className="border-t-2 border-gray2">
                        <div className="flex items-center border-b border-gray4">
                            <TableName className="h-12" required>
                                카테고리
                            </TableName>
                            <SelectBox
                                height="max-h-96"
                                full
                                className="w-full bg-white px-2"
                                placeholder="카테고리 선택"
                                options={categoryOptions}
                                value={selectedCategory}
                                onChange={value => {
                                    setSelectedCategory(value);
                                    setCategoryError(false);
                                }}
                                status={categoryError ? "error" : undefined}
                            />
                        </div>
                        <div className="flex items-center border-b border-gray4">
                            <TableName className="h-12 whitespace-nowrap">베스트플라워</TableName>
                            <SelectBox
                                height="max-h-96"
                                full
                                className="w-full bg-white px-2"
                                placeholder="선택"
                                options={codeOptions}
                                value={selectedCode}
                                onChange={value => {
                                    setSelectedCode(value);
                                }}
                            />
                        </div>

                        <div className="flex items-center border-b border-gray4">
                            <TableName className="h-12" required>
                                상품명
                            </TableName>
                            <Input
                                maxLength={30}
                                placeholder="30자"
                                className="w-full px-2"
                                value={productName}
                                status={productNameError ? "error" : undefined}
                                onChange={e => {
                                    setProductName(e.target.value);
                                    setProductNameError(false);
                                }}
                            />
                        </div>
                        <div className="flex items-center w-1/2">
                            <TableName required className="h-12">
                                가격
                            </TableName>
                            <Input
                                price
                                className="w-full px-2"
                                value={productAmount.toLocaleString()}
                                onChange={e => {
                                    const numericValue = Number(e.target.value.replace(/[^0-9]/g, ""));
                                    setProductAmount(numericValue);
                                }}
                            />
                        </div>
                        <div className="flex border-y border-gray4 items-stretch">
                            <TableName>
                                상품이미지
                                <br />
                                (최대 4장)
                            </TableName>
                            <div className="flex gap-2 flex-wrap p-2 w-full">
                                {productImages.length >= 1 && (
                                    <div
                                        onClick={() => {
                                            setImageProductUploadPop(true);
                                            setImageUploadPop(false);
                                        }}
                                        className={`${imageProductUploadPop ? "border-primary2 text-primary2" : "border-gray5 border-dashed text-grayicon"} cursor-pointer w-[60px] flex flex-col items-center border  rounded-xl p-2`}
                                    >
                                        <PlusIcon />
                                        <p className="text-sm ">추가</p>
                                    </div>
                                )}
                                {productImages.map((image, index) => (
                                    <div key={index} className="relative w-[60px] h-[60px] border rounded-xl overflow-hidden">
                                        <img
                                            onClick={() => handlePreviewImage(image)}
                                            src={URL.createObjectURL(image)}
                                            className="w-full h-full object-contain cursor-pointer"
                                            alt="상품 이미지"
                                        />
                                        <button
                                            className="w-6 h-6 absolute top-0 right-0 bg-black bg-opacity-60 text-white"
                                            onClick={() => handleRemoveImage(index, "product")}
                                        >
                                            <CloseIcon />
                                        </button>
                                    </div>
                                ))}
                                {productImages.length < 1 && (
                                    <div className="flex relative w-full">
                                        <div
                                            onClick={() => {
                                                setImageProductUploadPop(true);
                                                setImageUploadPop(false);
                                            }}
                                            className={`${imageProductUploadPop ? "border-primary2 text-primary2 bg-primary4" : "border-gray5 border-dashed text-grayicon"} cursor-pointer w-full flex flex-col items-center border  rounded-xl p-2`}
                                        >
                                            {imageProductUploadPop ? <UploadOnImage /> : <UploadImage />}
                                            <p className="text-sm">이미지 업로드</p>
                                        </div>
                                    </div>
                                )}
                                {imageProductUploadPop && (
                                    <div className="flex flex-col z-50 absolute w-[400px] bg-white rounded-2xl shadow-custom translate-y-16">
                                        <div className="flex w-full  items-center px-5 justify-between py-5 ">
                                            <p className="text-primary1 text-2xl font-bold">이미지 업로드</p>
                                            <p className="text-sm">최대 4장까지 업로드 가능합니다.</p>
                                            <CloseBlackIcon
                                                className="cursor-pointer"
                                                onClick={() => handleCloseImageUploadPop("product")}
                                            />
                                        </div>
                                        <div className="px-6 pb-5 flex flex-col gap-2">
                                            <label
                                                className="flex h-[160px] justify-center cursor-pointer flex-col items-center border border-gray5 border-dashed rounded-xl"
                                                onDrop={e => handleDrop(e, "product")}
                                                onDragOver={e => e.preventDefault()}
                                            >
                                                <UploadImage className="w-[72px] h-[72px] mb-2" />
                                                <p className="text-gray2 text-sm">이미지 업로드</p>
                                                <p className="text-gray3 text-sm">(이미지를 드래그하거나 복사, 붙여넣기하여 등록)</p>
                                                <input
                                                    type="file"
                                                    className="hidden"
                                                    multiple
                                                    onChange={e => handleImageUpload(e, "product")}
                                                />
                                            </label>
                                            <div className="flex gap-1 ">
                                                {tempProductImages.map((image, index) => (
                                                    <div
                                                        key={index}
                                                        className="relative w-[60px] h-[60px] border rounded-xl overflow-hidden"
                                                    >
                                                        <img
                                                            src={URL.createObjectURL(image)}
                                                            className="w-full h-full object-contain"
                                                            alt="상품 이미지"
                                                        />
                                                        <button
                                                            className="w-6 h-6 absolute top-0 right-0 bg-black bg-opacity-60 text-white"
                                                            onClick={() => handleRemoveImage(index, "product")}
                                                        >
                                                            <CloseIcon />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                            <Button onClick={() => handleConfirmImages("product")} size="L" status="regist">
                                                이미지 등록
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="flex border-b border-gray4 items-stretch">
                            <TableName>
                                구매시
                                <br /> 유의사항
                            </TableName>
                            <div className="flex gap-2 flex-wrap p-2 w-full">
                                {noticeImages.length >= 1 && (
                                    <div
                                        onClick={() => {
                                            setImageUploadPop(true);
                                            setImageProductUploadPop(false);
                                        }}
                                        className={`${imageUploadPop ? "border-primary2 text-primary2" : "border-gray5 border-dashed text-grayicon"} cursor-pointer w-[60px] flex flex-col items-center border  rounded-xl p-2`}
                                    >
                                        <PlusIcon />
                                        <p className="text-sm">추가</p>

                                        <input type="file" className="hidden" multiple onChange={e => handleImageUpload(e, "notice")} />
                                    </div>
                                )}
                                {noticeImages.map((image, index) => (
                                    <div key={index} className="relative w-[60px] h-[60px] border rounded-xl overflow-hidden">
                                        <img
                                            onClick={() => handlePreviewImage(image)}
                                            src={typeof image === "string" ? image : URL.createObjectURL(image)}
                                            className="w-full h-full object-contain cursor-pointer"
                                            alt="상품 이미지"
                                        />
                                        <button
                                            className="w-6 h-6 absolute top-0 right-0 bg-black bg-opacity-60 text-white"
                                            onClick={() => handleRemoveImage(index, "notice")}
                                        >
                                            <CloseIcon onClick={() => handleRemoveImage(index, "notice")} />
                                        </button>
                                    </div>
                                ))}
                                {noticeImages.length < 1 && (
                                    <div className="flex relative w-full">
                                        <div
                                            onClick={() => {
                                                setImageProductUploadPop(false);
                                                setImageUploadPop(true);
                                            }}
                                            className={`${imageUploadPop ? "border-primary2 text-primary2 bg-primary4" : "border-gray5 border-dashed text-grayicon"} cursor-pointer w-full flex flex-col items-center border  rounded-xl p-2`}
                                        >
                                            {imageUploadPop ? <UploadOnImage /> : <UploadImage />}
                                            <p className="text-sm ">이미지 업로드</p>
                                        </div>
                                    </div>
                                )}
                                {imageUploadPop && (
                                    <div className="flex flex-col z-50 absolute w-[400px] bg-white rounded-2xl shadow-custom translate-y-16">
                                        <div className="flex w-full  items-center px-5 justify-between py-5 ">
                                            <p className="text-primary1 text-2xl font-bold">이미지 업로드</p>
                                            <p className="text-sm">최대 4장까지 업로드 가능합니다.</p>
                                            <CloseBlackIcon
                                                className="cursor-pointer"
                                                onClick={() => handleCloseImageUploadPop("notice")}
                                            />
                                        </div>
                                        <div className="px-6 pb-5 flex flex-col gap-2">
                                            <label
                                                onDrop={e => handleDrop(e, "notice")}
                                                onDragOver={e => e.preventDefault()}
                                                className="flex h-[160px] justify-center cursor-pointer flex-col items-center border border-gray5 border-dashed rounded-xl"
                                            >
                                                <UploadImage className="w-[72px] h-[72px] mb-2" />
                                                <p className="text-gray2 text-sm">이미지 업로드</p>
                                                <p className="text-gray3 text-sm">(이미지를 드래그하거나 복사, 붙여넣기하여 등록)</p>
                                                <input
                                                    type="file"
                                                    className="hidden"
                                                    multiple
                                                    onChange={e => handleImageUpload(e, "notice")}
                                                />
                                            </label>
                                            <div className="flex gap-1 ">
                                                {tempNoticeImages.map((image, index) => (
                                                    <div
                                                        key={index}
                                                        className="relative w-[60px] h-[60px] border rounded-xl overflow-hidden"
                                                    >
                                                        <img
                                                            src={typeof image === "string" ? image : URL.createObjectURL(image)}
                                                            className="w-full h-full object-contain"
                                                            alt="상품 이미지"
                                                        />
                                                        <button
                                                            className="w-6 h-6 absolute top-0 right-0 bg-black bg-opacity-60 text-white"
                                                            onClick={() => handleRemoveImage(index, "notice")}
                                                        >
                                                            <CloseIcon />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                            <Button onClick={() => handleConfirmImages("notice")} size="L" status="regist">
                                                이미지 등록
                                            </Button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="flex border-b border-gray4 h-[120px] relative">
                            <TableName>상품소개</TableName>
                            <textarea
                                maxLength={1000}
                                value={productContent}
                                onChange={e => setProductContent(e.target.value)}
                                className="border m-2 rounded-lg w-full resize-none outline-0 py-1.5 px-3 text-sm"
                            />
                            <div className="absolute bottom-3 right-4 text-xs text-gray2">{productContent.length}/1000</div>
                        </div>

                        <div className="flex items-center border-b border-gray4">
                            <TableName className="h-12">메모</TableName>
                            <Input
                                maxLength={100}
                                className="w-full px-2"
                                value={productMemo}
                                onChange={e => setProductMemo(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="flex w-full gap-2">
                        <Button size="L" className="w-full" onClick={handleCancel}>
                            취소
                        </Button>
                        <Button size="L" status="regist" className="w-full" onClick={handleSave}>
                            저장
                        </Button>
                    </div>
                    {previewImage && (
                        <div
                            className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-50"
                            onClick={() => setPreviewImage(null)}
                        >
                            <div className="w-[920px] bg-white rounded-2xl shadow-custom">
                                <div className="flex items-center justify-between px-6 py-5">
                                    <p className="text-2xl font-bold">이미지 크게 보기</p>
                                    <CloseBlackIcon className="cursor-pointer" onClick={() => setPreviewImage(null)} />
                                </div>
                                <div className="flex p-5  items-center justify-center ">
                                    <img src={previewImage} alt="preview" className="object-contain max-h-[500px]" />
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NewGoods;
